// ===========================================
// The invoice's existing discounts, in the amend dialog (2026-09-28)
// ===========================================
// Andrew, 2026-09-28: "the applied discounts aren't displaying when amending an
// invoice", then: an amendment may also REMOVE one. Each existing discount is
// listed with its current amount and — from the server's live preview — what it
// becomes; one marked for removal is struck through (with Undo) and comes off
// when the amendment is saved (needs operations_pricing:delete).
//
// Preview amounts are matched by the discount's id (snapshots carry it since
// 2026-09-28), so a removal does not shift the others' figures.

import { Box, Button, Typography } from '@mui/material';
import type { InvoiceDiscount, InvoiceSnapshot } from '../../api/invoices.api';
import { formatCurrencyFromDollars as money } from '../../utils/currency';
import { appliedCoverageSuffix } from '../../utils/discountCoverage';

interface AmendExistingDiscountsProps {
  discounts: InvoiceDiscount[];
  /** The preview's discounts, when it has answered for the current form. */
  previewDiscounts?: InvoiceSnapshot['discounts'];
  /** Ids marked for removal. */
  removedIds: string[];
  /** Offered only with operations_pricing:delete; omitted = no Remove buttons. */
  onToggleRemove?: (discountId: string) => void;
}

export default function AmendExistingDiscounts({
  discounts,
  previewDiscounts,
  removedIds,
  onToggleRemove,
}: AmendExistingDiscountsProps) {
  if (discounts.length === 0) return null;

  return (
    <Box data-testid="amend-existing-discounts">
      <Typography variant="subtitle2">Discounts on this invoice</Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {discounts.map((d) => {
          const removed = removedIds.includes(d.id);
          const current = Number(d.calculatedAmount);
          const next = previewDiscounts?.find((p) => p.id === d.id)?.calculatedAmount;
          const changes = !removed && next !== undefined && Number(next) !== current;
          return (
            <Box component="li" key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                sx={removed ? { textDecoration: 'line-through', color: 'text.secondary' } : undefined}
              >
                {d.name}
                {d.isPercent ? ` (${Number(d.value)}%)` : ''}
                {appliedCoverageSuffix(d)} −{money(current)}
                {changes && <> → −{money(next!)}</>}
              </Typography>
              {removed && (
                <Typography variant="caption" color="error">
                  Will be removed
                </Typography>
              )}
              {onToggleRemove && (
                <Button size="small" onClick={() => onToggleRemove(d.id)} aria-label={`${removed ? 'Undo remove' : 'Remove'} ${d.name}`}>
                  {removed ? 'Undo' : 'Remove'}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Discounts you keep stay on the invoice and are re-calculated against the lines you save.
      </Typography>
    </Box>
  );
}
